import { useRef, useState } from 'react';
import { useGameStore } from '../core/store/gameStore';
import { convertSkpToGlb } from '../components/import/skpConvert';

// ============================================================================
// ImportPanel - the "导入模型" entry point.
//
//  * .skp   parsed right in the browser (openskp Web Worker) into an in-memory
//           GLB - no SketchUp, no server round-trip. Textures are embedded.
//  * .glb / .gltf  loaded directly (self-contained .glb recommended; the
//           optional desktop exporter in tools/sketchup-exporter is the
//           fallback for files the in-browser parser cannot handle).
//  * Once a model is placed a small toolbar offers rotate / resize / walk-to /
//    remove controls that act on the model standing in the meadow.
// ============================================================================

const EXT_GLB = '.glb';
const EXT_GLTF = '.gltf';

function toolbarBtn(base: React.CSSProperties, active = false): React.CSSProperties {
    return {
        ...base,
        background: active ? 'rgba(120, 90, 40, 0.75)' : 'rgba(22, 28, 34, 0.72)',
    };
}

export function ImportPanel() {
    const fileRef = useRef<HTMLInputElement>(null);

    const importedModel = useGameStore((state) => state.importedModel);
    const setImportedModel = useGameStore((state) => state.setImportedModel);
    const importError = useGameStore((state) => state.importError);
    const setImportError = useGameStore((state) => state.setImportError);
    const setImportRot = useGameStore((state) => state.setImportRot);
    const setImportScale = useGameStore((state) => state.setImportScale);
    const requestImportView = useGameStore((state) => state.requestImportView);
    const isMobile = useGameStore((state) => state.isMobile);
    const [busy, setBusy] = useState<string | null>(null);

    const baseBtn: React.CSSProperties = {
        pointerEvents: 'auto',
        cursor: 'pointer',
        border: '1px solid rgba(255,255,255,0.16)',
        borderRadius: '8px',
        color: '#fff',
        fontFamily: 'system-ui, "PingFang SC", "Microsoft YaHei", sans-serif',
        fontSize: isMobile ? '13px' : '14px',
        backdropFilter: 'blur(6px)',
        boxShadow: '0 4px 18px rgba(0,0,0,0.35)',
        padding: isMobile ? '6px 10px' : '7px 13px',
        letterSpacing: '0.5px',
        userSelect: 'none',
    };

    const pickFile = () => fileRef.current?.click();

    const installGlb = (url: string, name: string) => {
        const prev = useGameStore.getState().importedModel;
        if (prev?.url && prev.url !== url) URL.revokeObjectURL(prev.url);
        // fresh import starts from a clean turntable transform
        useGameStore.setState({ importScaleMul: 1, importRotY: 0 });
        setImportedModel({ url, name });
        setImportError(null);
    };

    const onFile = async (file: File) => {
        const lower = file.name.toLowerCase();
        if (!file) return;

        if (lower.endsWith('.skp')) {
            setBusy('解析 .skp 中…');
            setImportError(null);
            try {
                const res = await convertSkpToGlb(file);
                installGlb(res.url, res.name);
            } catch (err) {
                setImportError((err as Error).message);
            } finally {
                setBusy(null);
            }
            return;
        }

        if (!lower.endsWith(EXT_GLB) && !lower.endsWith(EXT_GLTF)) {
            setImportError('请选择 .skp / .glb / .gltf 文件。');
            return;
        }

        setBusy('读取中…');
        setImportError(null);
        try {
            const url = URL.createObjectURL(file);
            installGlb(url, file.name);
        } finally {
            setBusy(null);
        }
    };

    const removeModel = () => {
        const prev = useGameStore.getState().importedModel;
        setImportedModel(null);
        if (prev?.url) URL.revokeObjectURL(prev.url);
        // reset toolbar transform
        setImportScale(1 / useGameStore.getState().importScaleMul);
        useGameStore.setState({ importRotY: 0, importScaleMul: 1 });
    };

    return (
        <>
            {/* Entry button (top-left cluster) */}
            <div style={{
                position: 'fixed',
                top: '12px',
                left: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '8px',
                zIndex: 60,
            }}>
                <button
                    onClick={pickFile}
                    disabled={busy !== null}
                    style={{
                        ...baseBtn,
                        borderColor: importedModel ? 'rgba(255, 214, 140, 0.55)' : 'rgba(255,255,255,0.18)',
                        color: importedModel ? '#ffe3ad' : '#fff',
                        opacity: busy !== null ? 0.6 : 1,
                    }}
                >
                    {busy ?? (importedModel ? '替换模型' : '导入 SketchUp 模型')}
                </button>
                {importedModel && (
                    <span style={{
                        color: 'rgba(255,255,255,0.62)',
                        fontFamily: 'Cousine, monospace',
                        fontSize: '11px',
                        maxWidth: '220px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        background: 'rgba(0,0,0,0.3)',
                        borderRadius: '6px',
                        padding: '2px 8px',
                    }}>
                        {importedModel.name}
                    </span>
                )}
            </div>

            {/* Hidden file input */}
            <input
                ref={fileRef}
                type="file"
                accept=".skp,.glb,.gltf"
                style={{ display: 'none' }}
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onFile(f);
                    e.target.value = '';
                }}
            />

            {/* Toolbar for the placed model */}
            {importedModel && (
                <div style={{
                    position: 'fixed',
                    bottom: '78px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    padding: '8px 12px',
                    borderRadius: '26px',
                    background: 'rgba(14, 18, 22, 0.6)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    backdropFilter: 'blur(8px)',
                    pointerEvents: 'auto',
                    zIndex: 60,
                }}>
                    <button style={toolbarBtn(baseBtn)} onClick={() => setImportRot(-Math.PI / 8)} title="左转">⟲ 旋转</button>
                    <button style={toolbarBtn(baseBtn)} onClick={() => setImportRot(Math.PI / 8)} title="右转">旋转 ⟳</button>
                    <button style={toolbarBtn(baseBtn)} onClick={() => setImportScale(1.18)} title="放大">＋ 放大</button>
                    <button style={toolbarBtn(baseBtn)} onClick={() => setImportScale(1 / 1.18)} title="缩小">缩小 －</button>
                    <button style={toolbarBtn(baseBtn)} onClick={() => requestImportView()} title="传送到模型前">前往查看</button>
                    <button
                        style={{ ...toolbarBtn(baseBtn), background: 'rgba(110, 40, 30, 0.7)' }}
                        onClick={removeModel}
                        title="从场景中移除"
                    >
                        移除
                    </button>
                </div>
            )}

            {/* Error toast */}
            {importError && (
                <div style={{
                    position: 'fixed',
                    top: '64px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    maxWidth: 'min(600px, 92vw)',
                    background: 'rgba(80, 24, 18, 0.88)',
                    border: '1px solid rgba(255,120,90,0.5)',
                    color: '#ffd9cf',
                    borderRadius: '12px',
                    padding: '10px 16px',
                    fontSize: '13px',
                    lineHeight: 1.5,
                    fontFamily: 'system-ui, "PingFang SC", "Microsoft YaHei", sans-serif',
                    backdropFilter: 'blur(6px)',
                    pointerEvents: 'auto',
                    zIndex: 70,
                    whiteSpace: 'pre-wrap',
                }}>
                    <span>{importError}</span>
                    <button
                        onClick={() => setImportError(null)}
                        style={{
                            marginLeft: '12px',
                            pointerEvents: 'auto',
                            cursor: 'pointer',
                            background: 'transparent',
                            border: 'none',
                            color: '#ffb39f',
                            fontSize: '13px',
                        }}
                    >
                        关闭
                    </button>
                </div>
            )}
        </>
    );
}
